import React from 'react';
import { ShowPageLayout, ShowSection } from 'framework/ui/layout';
import { FormShowTemplate, FormFieldsData } from 'framework/form';
import { Typography, Link } from '@mui/material';
import ProjectBillingForm from './ProjectBillingForm';
import { buildErrorObject, createErrorResponse, validateFormData } from '../helpers/projectBillingHelper';
import useProjectBillingService from '../useProjectBillingService';
import { useShowContext } from 'framework/show';
import { BillingFormData } from '../types';
import { useNewProjectUrl } from 'domain/shared/newProject';
import { useAccountUrl } from 'domain/shared/account';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import BillingSOWFileLink from '../shared/fields/BillingSOWFileLink';

const BillingContent = () => {
  const { getSingleEntityUrl, getFinanceUrl } = useNewProjectUrl();
  const { getSingleEntityUrl: getAccountUrl } = useAccountUrl();
  const { data } = useShowContext<BillingFormData>();
  const navigate = useNavigate();
  const billingData = data.getData();

  const { projectId, projectName, sowId, accountId, billingType } = billingData;

  const hasHeaderInfo = projectName || sowId || accountId;

  return (
    <ShowPageLayout
      entityHeader={
        <Typography display="flex" alignItems="center" gap="9px" variant="h2" color="brand">
          {hasHeaderInfo ? (
            <>
              Billing Information for
              {projectName && (
                <Link
                  component={RouterLink}
                  target="_blank"
                  to={getSingleEntityUrl(projectId)}
                  color="brand"
                  underline="none"
                >
                  {projectName}
                </Link>
              )}
              {projectName && sowId && '/'}
              {sowId && <BillingSOWFileLink />}
              {(projectName || sowId) && accountId && '/'}
              {accountId && (
                <Link
                  component={RouterLink}
                  target="_blank"
                  to={getAccountUrl(accountId)}
                  color="brand"
                  underline="none"
                >
                  {accountId}
                </Link>
              )}
            </>
          ) : (
            'Billing Information'
          )}
        </Typography>
      }
    >
      {billingType && (
        <ShowSection sx={{ m: '0 !important' }}>
          <ProjectBillingForm onCancelAction={() => navigate(getFinanceUrl(projectId))} />
        </ShowSection>
      )}
    </ShowPageLayout>
  );
};

const BillingInfoFormLayout: React.FC = () => {
  const { getFinanceUrl } = useNewProjectUrl();
  const { billingSchedule, getFormData } = useProjectBillingService();

  const submitFormFunc = async (data: FormFieldsData): Promise<void> => {
    const { calendarErrors, customErrors } = validateFormData(data);

    if (calendarErrors.length || customErrors.length) {
      const errors = buildErrorObject(calendarErrors, customErrors);

      throw createErrorResponse(errors);
    }

    return await billingSchedule(data);
  };

  return (
    <FormShowTemplate
      getDataFunc={getFormData}
      submitFormFunc={submitFormFunc}
      afterSubmit={{
        redirect: getFinanceUrl(),
        message: 'You are awesome! The billing schedule has been successfully saved.',
      }}
    >
      <BillingContent />
    </FormShowTemplate>
  );
};

export default BillingInfoFormLayout;
